/**
 * Export Utilities
 * Functions to export expenses to CSV and PDF formats
 */

/**
 * Export expenses to CSV format
 * @param {Array} expenses - Array of expense objects
 * @param {String} filename - Name of the CSV file to download
 */
export const exportToCSV = (expenses, filename = 'expenses.csv') => {
  if (!expenses || expenses.length === 0) {
    alert('No expenses to export');
    return;
  }

  try {
    // Define CSV headers
    const headers = [
      'Date',
      'Vendor Name',
      'Category',
      'Amount',
      'Description',
      'Source',
      'Tags',
      'Line Items'
    ];

    // Convert expenses to CSV rows
    const rows = expenses.map(expense => [
      expense.date || '',
      expense.vendor_name || '',
      expense.category || '',
      expense.amount || '',
      (expense.description || '').replace(/"/g, '""'), // Escape quotes
      getSourceType(expense),
      (expense.tags ? expense.tags.join('; ') : '').replace(/"/g, '""'),
      formatLineItems(expense.line_items)
    ]);

    // Combine headers and rows
    const csvContent = [
      headers.map(h => `"${h}"`).join(','),
      ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
    ].join('\n');

    // Create blob and download
    downloadFile(csvContent, filename, 'text/csv;charset=utf-8;');
  } catch (error) {
    console.error('Error exporting to CSV:', error);
    alert('Failed to export to CSV: ' + error.message);
  }
};

/**
 * Export expenses to PDF format
 * @param {Array} expenses - Array of expense objects
 * @param {String} filename - Name of the PDF file to download
 */
export const exportToPDF = (expenses, filename = 'expenses.pdf') => {
  if (!expenses || expenses.length === 0) {
    alert('No expenses to export');
    return;
  }

  try {
    // Create HTML table content
    const htmlContent = generatePDFHtml(expenses);

    // Use dynamic import to avoid issues if library isn't immediately available
    import('html2pdf.js').then(({ default: html2pdf }) => {
      const options = {
        margin: 10,
        filename: filename,
        image: { type: 'jpeg', quality: 0.98 },
        html2canvas: { scale: 2 },
        jsPDF: { orientation: 'landscape', unit: 'mm', format: 'a4' },
        pagebreak: { mode: ['avoid-all', 'css'] }
      };

      html2pdf()
        .set(options)
        .from(htmlContent)
        .save();
    }).catch(() => {
      // Fallback: if html2pdf not available, use print functionality
      fallbackPrintToPDF(expenses, filename);
    });
  } catch (error) {
    console.error('Error exporting to PDF:', error);
    alert('Failed to export to PDF: ' + error.message);
  }
};

/**
 * Generate HTML content for PDF export
 * @param {Array} expenses - Array of expense objects
 * @returns {HTMLElement} - HTML element for PDF
 */
const generatePDFHtml = (expenses) => {
  const div = document.createElement('div');
  div.style.width = '100%';
  div.style.padding = '20px';
  div.style.fontFamily = 'Arial, sans-serif';
  div.style.fontSize = '12px';

  // Title
  const title = document.createElement('h1');
  title.textContent = 'Expense Report';
  title.style.textAlign = 'center';
  title.style.marginBottom = '10px';
  title.style.fontSize = '24px';
  div.appendChild(title);

  // Date range
  const dateRange = document.createElement('p');
  dateRange.textContent = `Generated on: ${new Date().toLocaleDateString()}`;
  dateRange.style.textAlign = 'center';
  dateRange.style.marginBottom = '20px';
  dateRange.style.color = '#666';
  div.appendChild(dateRange);

  // Summary stats (use amount_in_default_currency for accurate multi-currency totals)
  const totalAmount = expenses.reduce((sum, exp) => sum + parseFloat(exp.amount_in_default_currency ?? exp.amount ?? 0), 0);
  const summary = document.createElement('p');
  summary.style.marginBottom = '20px';
  summary.style.fontWeight = 'bold';
  summary.innerHTML = `<strong>Total Expenses:</strong> ${expenses.length} &nbsp;&nbsp; <strong>Total Amount:</strong> $${totalAmount.toFixed(2)}`;
  div.appendChild(summary);

  // Table
  const table = document.createElement('table');
  table.style.width = '100%';
  table.style.borderCollapse = 'collapse';
  table.style.marginTop = '20px';

  // Table headers
  const thead = document.createElement('thead');
  const headerRow = document.createElement('tr');
  headerRow.style.backgroundColor = '#f0f0f0';
  headerRow.style.fontWeight = 'bold';
  headerRow.style.borderBottom = '2px solid #333';

  const headers = ['Date', 'Vendor', 'Category', 'Amount', 'Description', 'Source'];
  headers.forEach(header => {
    const th = document.createElement('th');
    th.textContent = header;
    th.style.padding = '10px';
    th.style.textAlign = 'left';
    th.style.borderBottom = '1px solid #ddd';
    headerRow.appendChild(th);
  });
  thead.appendChild(headerRow);
  table.appendChild(thead);

  // Table body
  const tbody = document.createElement('tbody');
  expenses.forEach((expense, index) => {
    const row = document.createElement('tr');
    row.style.backgroundColor = index % 2 === 0 ? '#ffffff' : '#f9f9f9';
    row.style.breakInside = 'avoid';

    const cells = [
      expense.date,
      expense.vendor_name,
      expense.category,
      `$${parseFloat(expense.amount || 0).toFixed(2)}`,
      expense.description || '',
      getSourceType(expense)
    ];

    cells.forEach(cellText => {
      const td = document.createElement('td');
      td.textContent = cellText;
      td.style.padding = '8px';
      td.style.borderBottom = '1px solid #ddd';
      row.appendChild(td);
    });

    tbody.appendChild(row);
  });
  table.appendChild(tbody);
  div.appendChild(table);

  return div;
};

/**
 * Fallback PDF export using browser print dialog
 * @param {Array} expenses - Array of expense objects
 * @param {String} filename - Name of the PDF file
 */
const fallbackPrintToPDF = (expenses, filename) => {
  const printWindow = window.open('', '', 'width=900,height=600');

  // Build HTML for print
  let html = `
    <html>
      <head>
        <title>${filename}</title>
        <style>
          body { font-family: Arial, sans-serif; margin: 20px; }
          h1 { text-align: center; }
          table { width: 100%; border-collapse: collapse; margin-top: 20px; }
          th { background-color: #f0f0f0; font-weight: bold; padding: 10px; text-align: left; border-bottom: 2px solid #333; }
          td { padding: 8px; border-bottom: 1px solid #ddd; }
          tr { break-inside: avoid; }
          tr:nth-child(even) { background-color: #f9f9f9; }
        </style>
      </head>
      <body>
        <h1>Expense Report</h1>
        <p style="text-align: center; color: #666;">Generated on: ${new Date().toLocaleDateString()}</p>
        <table>
          <thead>
            <tr>
              <th>Date</th>
              <th>Vendor</th>
              <th>Category</th>
              <th>Amount</th>
              <th>Description</th>
              <th>Source</th>
            </tr>
          </thead>
          <tbody>
  `;

  expenses.forEach(expense => {
    html += `
      <tr>
        <td>${expense.date}</td>
        <td>${expense.vendor_name}</td>
        <td>${expense.category}</td>
        <td>$${parseFloat(expense.amount || 0).toFixed(2)}</td>
        <td>${expense.description || ''}</td>
        <td>${getSourceType(expense)}</td>
      </tr>
    `;
  });

  // Use amount_in_default_currency for accurate multi-currency totals
  const totalAmount = expenses.reduce((sum, exp) => sum + parseFloat(exp.amount_in_default_currency ?? exp.amount ?? 0), 0);

  html += `
          </tbody>
        </table>
        <p style="margin-top: 20px; font-weight: bold;">
          Total Expenses: ${expenses.length} &nbsp;&nbsp; Total Amount: $${totalAmount.toFixed(2)}
        </p>
        <script>
          window.print();
        </script>
      </body>
    </html>
  `;

  printWindow.document.write(html);
  printWindow.document.close();
};

/**
 * Determine the source of an expense (Upload, Statement, Manual)
 * @param {Object} expense - Expense object
 * @returns {String} - Source type
 */
const getSourceType = (expense) => {
  if (expense.description && expense.description.includes('[Email Forward]')) {
    return 'Email';
  } else if (expense.receipt_image_url) {
    return 'Upload';
  } else if (expense.raw_ocr_text && !expense.receipt_image_url) {
    return 'Statement';
  } else {
    return 'Manual';
  }
};

/**
 * Format line items for display
 * @param {Array} lineItems - Array of line items
 * @returns {String} - Formatted line items string
 */
const formatLineItems = (lineItems) => {
  if (!lineItems || !Array.isArray(lineItems) || lineItems.length === 0) {
    return '';
  }

  return lineItems
    .map(item => `${item.name} (${item.quantity || 1}x $${(item.price || 0).toFixed(2)})`)
    .join('; ');
};

/**
 * Download file utility
 * @param {String|Blob} content - File content
 * @param {String} filename - Name of the file
 * @param {String} mimeType - MIME type of the file
 */
const downloadFile = (content, filename, mimeType) => {
  const blob = new Blob([content], { type: mimeType });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

/**
 * Get formatted filename with current date
 * @param {String} format - 'csv' or 'pdf'
 * @returns {String} - Formatted filename
 */
export const getFormattedFilename = (format = 'csv') => {
  const today = new Date();
  const dateStr = today.toISOString().split('T')[0]; // YYYY-MM-DD
  return `expenses_${dateStr}.${format}`;
};
